import { obterElegiveisParaProcurador } from '@/features/proposta/entities/Comprador/opcoes-comprador-menu'
import type { TCompradoresCadastrados } from '@/features/proposta/interfaces/proposta.type'
import type { TProcuradorPropostaSchema } from '@/features/proposta/schemas'
import {
  compradorPessoaFisicaStepSchema,
  compradorVinculoSchema,
  type CompradorPessoaFisicaStepSchemaInput,
  type CompradorVinculoStepSchemaInput,
} from '@/features/proposta/schemas/compradores/comprador-pf.schema'
import {
  compradorPessoaJuridicaSchema,
  type CompradorPessoaJuridicaSchemaInput,
} from '@/features/proposta/schemas/compradores/comprador-pj.schema'
import { tipoAssinaturaEnum } from '@/types/proposta'

const stepsPFMaiorIdade = ['dadosPF', 'contatosPF', 'estadoCivil'] as const

const stepsMenorIdade = ['dadosPF', 'dadosMaePai', 'contatosMaePai', 'estadoCivilMaePai'] as const
const stepsMaePai = ['vincularCompradorMenor', 'dadosMaePai', 'contatosMaePai', 'estadoCivilMaePai'] as const

const stepsPJ = ['dadosPJ'] as const
const stepsRepresentanteLegal = [
  'dadosPJ',
  'tipoResponsavel',
  'dadosRepresentanteLegal',
  'contatosRepresentanteLegal',
  'estadoCivilRepresentanteLegal',
] as const
const stepsRepresentanteLegalParaVinculo = [
  'vincularCompradorParaRepresentanteLegal',
  'dadosRepresentanteLegal',
  'contatosRepresentanteLegal',
  'estadoCivilRepresentanteLegal',
] as const

const stepsProcurador = [
  'dadosPJ',
  'tipoResponsavel',
  'dadosProcurador',
  'contatosProcurador',
  'estadoCivilProcurador',
  'vincularCompradorParaProcurador',
] as const
const stepsProcuradorParaVinculo = [
  'escolherClienteExistente',
  'dadosProcurador',
  'contatosProcurador',
  'estadoCivilProcurador',
  'vincularCompradorParaProcurador',
] as const

const stepsConjuge = ['dadosPF', 'contatosPF', 'estadoCivil', 'dadosConjuge', 'contatosConjuge'] as const

export type StepName =
  | (typeof stepsMenorIdade)[number]
  | (typeof stepsMaePai)[number]
  | (typeof stepsPFMaiorIdade)[number]
  | (typeof stepsPJ)[number]
  | (typeof stepsRepresentanteLegal)[number]
  | (typeof stepsRepresentanteLegalParaVinculo)[number]
  | (typeof stepsProcurador)[number]
  | (typeof stepsProcuradorParaVinculo)[number]
  | (typeof stepsConjuge)[number]

export const stepsFlow = {
  compradorPessoaFisica: {
    schema: compradorPessoaFisicaStepSchema,
    conditionalSteps: (comprador: CompradorPessoaFisicaStepSchemaInput): readonly StepName[] => {
      if (comprador.fluxoIndependente === 'MaePai')
        return comprador.mode === 'edit' ? stepsMaePai.slice(1) : stepsMaePai

      if (comprador.fluxoIndependente === 'Conjuge' && comprador.mode === 'edit') return stepsConjuge.slice(-2)

      if (comprador.dados.maiorDe18Anos === 'nao') {
        return comprador.mode === 'edit' ? [stepsMenorIdade[0]] : stepsMenorIdade
      }
      if (
        comprador.tipo === 'PessoaFisica' &&
        comprador.estadoCivil?.tipoAssinatura === tipoAssinaturaEnum.Dupla &&
        comprador.mode !== 'edit' &&
        comprador.dados.maiorDe18Anos === 'sim' &&
        !comprador.fluxoIndependente
      ) {
        return stepsConjuge
      }

      return stepsPFMaiorIdade
    },
  },
  compradorPessoaJuridica: {
    schema: compradorPessoaJuridicaSchema,
    conditionalSteps: (comprador: CompradorPessoaJuridicaSchemaInput): readonly StepName[] => {
      if (comprador.tipo === 'PessoaJuridica' && comprador.mode === 'edit') return stepsPJ
      if (comprador.tipo === 'PessoaJuridica' && comprador.responsavel?.tipo === 'Representante') {
        return stepsRepresentanteLegal
      }
      if (comprador.tipo === 'PessoaJuridica' && comprador.responsavel?.tipo === 'Procurador') {
        return stepsProcurador.slice(0, -1)
      }
      return stepsRepresentanteLegal
    },
  },
  procurador: {
    schema: compradorVinculoSchema,
    conditionalSteps: (
      vinculo: CompradorVinculoStepSchemaInput,
      compradores?: TCompradoresCadastrados,
      procuradores?: TProcuradorPropostaSchema[],
    ): readonly StepName[] => {
      if (vinculo.mode === 'edit' && vinculo.tipo === 'Procurador') {
        return stepsProcuradorParaVinculo.slice(1)
      }
      if (vinculo.tipo === 'Procurador' && obterElegiveisParaProcurador(compradores, procuradores).length > 1) {
        return stepsProcuradorParaVinculo
      }

      if (vinculo.tipo === 'Procurador' && obterElegiveisParaProcurador(compradores, procuradores).length <= 1) {
        return stepsProcuradorParaVinculo.slice(1)
      }
      return []
    },
  },
  representanteLegal: {
    schema: compradorVinculoSchema,
    conditionalSteps: (vinculo: CompradorVinculoStepSchemaInput): readonly StepName[] => {
      if (vinculo.mode === 'edit' && vinculo.tipo === 'Representante')
        return stepsRepresentanteLegalParaVinculo.slice(1)

      return stepsRepresentanteLegalParaVinculo
    },
  },
}
